import { Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment-timezone';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { appModuleAnimation } from '@shared/animations/routerTransition';
import { AppComponentBase } from '@shared/app-component-base';
import { StringUtils } from '@shared/helpers/StringUtils';
import { CartaoCreditoAuxiliarModel } from '@shared/model/cartao-credito-auxiliar.model';
import { AssinaturaDto, AssinaturaServiceProxy, CartaoCreditoDto, CreateAssinaturaDto, EnderecoAssinaturaDto, PlanoDto, PlanoServiceProxy, TipoPagamentoEnum } from '@shared/service-proxies/service-proxies';
import { TipoAssinaturaService } from '@shared/services/tipo-assinatura.service';
import { ViewSituacaoPagamentoDialogComponent } from '@shared/views/view-situacao-pagamento/view-situacao-pagamento-dialog.component';
import { BuscaCepService } from '@shared/services/busca-cep.service';
import { MetodosPagamentosComponent } from '@shared/components/metodos-pagamentos/metodos-pagamentos.component';
import { Location } from '@angular/common';

@Component({
  selector: 'app-realizar-pagamento',
  templateUrl: './realizar-pagamento.component.html',
  styleUrls: ['./realizar-pagamento.component.scss'],
  animations: [appModuleAnimation()],
})
export class RealizarPagamentoComponent extends AppComponentBase implements OnInit, OnDestroy {
  protected saving: boolean = false;
  protected plano: PlanoDto;
  protected dataAtual = moment();
  protected dataVencimento: moment.Moment;
  protected pagamentoSelecionado: string;
  protected isAnual: boolean;
  protected cartaoCredito: CartaoCreditoAuxiliarModel = new CartaoCreditoAuxiliarModel();
  protected endereco: EnderecoAssinaturaDto = new EnderecoAssinaturaDto();
  private id: string;
  private assinatura: CreateAssinaturaDto = new CreateAssinaturaDto;
  private rota: string;
  desabilitarCamposEndereco: boolean = true;
  subscriptions: Subscription[] = [];
  @ViewChild("metodosPagamento") metodoPagamento: MetodosPagamentosComponent;
  mostrarVoltar: boolean = false;

  constructor(
    injector: Injector,
    private readonly _planoService: PlanoServiceProxy,
    private readonly _assinaturaService: AssinaturaServiceProxy,
    private readonly _tipoAssinaturaService: TipoAssinaturaService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private _modalService: BsModalService,
    private location: Location,
    private readonly _buscaCepService: BuscaCepService,

  ) { super(injector) }

  ngOnDestroy(): void {
    this._tipoAssinaturaService.clear();
    this.subscriptions.forEach((sb) => sb.unsubscribe());
  }

  ngOnInit(): void {
    this.rota = this.getRota();
    this.isAnual = this._tipoAssinaturaService.valor;
    this._route.queryParams.subscribe(params => {
    this.mostrarVoltar = params['origem'] === 'minha-assinatura';
  });
    this._route.params.subscribe(params => {
      const id = params['id'];
      this.id = id;
      this.getPlano();
    });
    if (this.isAnual)
      this.dataVencimento = moment().add('year', 1);
    else this.dataVencimento = moment().add('month', 1);
  }

  private getPlano(): void {
    abp.ui.setBusy();
    this._planoService.get(this.id).pipe(
      finalize(() => {
        abp.ui.clearBusy();
      })
    ).subscribe((result) => {
      this.plano = result;
    });
  }

  protected selectPagamento(value: string): void {
    this.pagamentoSelecionado = value;
  }

  protected realizarPagamento(): void {
    if (!this.plano.free) {
      this.pagamentoSelecionado = this.metodoPagamento?.pagamentoSelecionado;
      this.endereco = this.metodoPagamento.endereco;
      this.cartaoCredito = this.metodoPagamento.cartaoCredito;
    }
    if (!this.pagamentoSelecionado && !this.plano.free) {
      abp.notify.info("Selecione a forma de pagamento!");
      return;
    }

    if (this.pagamentoSelecionado == "CARTAO" && this.validarCartao()) return;
    if (this.pagamentoSelecionado == "BOLETO" && this.validarEndereco()) return;
    abp.ui.setBusy();
    this.assinatura.ehAnual = this.isAnual;
    this.assinatura.planoId = this.plano.id;
    this.assinatura.endereco = this.endereco;
    this.setFormaPagamento();

    if (this.pagamentoSelecionado === "CARTAO") {
      this.getDadosCartao()
        .then((sucesso) => {
          if (sucesso) {
            this.create();
          } else {
            abp.notify.info("Verifique os dados do cartão!");
          }
        })
        .catch((erro) => {
          console.error('Erro geral ao obter dados do cartão:', erro);
        });
    } else {
      this.create();
    }
  }

  private create(): void {
    abp.ui.setBusy();
    this._assinaturaService.create(this.assinatura).pipe(
      finalize(() => {
        abp.ui.clearBusy();
      })
    ).subscribe((result) => {
      this.appSession.init(this.appSession.tenantName).then(() => {
        abp.notify.success("Assinatura atualizada com sucesso!");
        this.visualizarSituacaoPagamento(this.appSession.userId, null)
        this._router.navigate(["app", this.rota, "minha-assinatura"]);
      })
    });
  }

  private setFormaPagamento(): void {
    if (this.pagamentoSelecionado == "PIX") this.assinatura.tipoPagamento = TipoPagamentoEnum._0
    else if (this.pagamentoSelecionado == "BOLETO") this.assinatura.tipoPagamento = TipoPagamentoEnum._1
    else if (this.pagamentoSelecionado == "CARTAO") this.assinatura.tipoPagamento = TipoPagamentoEnum._2
    else if (this.plano.free) this.assinatura.tipoPagamento = TipoPagamentoEnum._4

    this.assinatura.cartaoCredito = new CartaoCreditoDto({
      cardToken: "",
      cpf: "",
      emissor: "",
      nome: "",
    });
  }

  protected validarCartao() {
    const validations = [
      { condition: StringUtils.isNullOrEmpty(this.cartaoCredito.ccv), message: "Informe o ccv do cartão!" },
      { condition: StringUtils.isNullOrEmpty(this.cartaoCredito.cpf), message: "Informe o cpf!" },
      { condition: StringUtils.isNullOrEmpty(this.cartaoCredito.nome), message: "Informe o nome!" },
      { condition: StringUtils.isNullOrEmpty(this.cartaoCredito.numeroCartao), message: "Informe o número do cartão!" },
      { condition: StringUtils.isNullOrEmpty(this.cartaoCredito.validade), message: "Informe a vaidade do cartão!" },
    ];
    this.showErrorMenssage(validations);
    return validations.some(x => x.condition);
  }

  protected validarEndereco() {
    const validations = [
      { condition: StringUtils.isNullOrEmpty(this.endereco.zipCode), message: "Informe o cep!" },
      { condition: StringUtils.isNullOrEmpty(this.endereco.city), message: "Informe a cidade!" },
      { condition: StringUtils.isNullOrEmpty(this.endereco.federalUnit), message: "Informe o estado!" },
      { condition: StringUtils.isNullOrEmpty(this.endereco.neighborhood), message: "Informe a rua!" },
      { condition: StringUtils.isNullOrEmpty(this.endereco.streetName), message: "Informe o nome da rua!" },
      { condition: StringUtils.isNullOrEmpty(this.endereco.streetNumber), message: "Informe o número!" },
    ];
    this.showErrorMenssage(validations);
    return validations.some(x => x.condition);
  }

  private showErrorMenssage(validations): void {
    for (const validation of validations) {
      if (validation.condition) {
        abp.notify.warn(validation.message);
      }
    }
  }

  private getDadosCartao(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      let mes = '';
      let ano = '';

      if (this.cartaoCredito.validade && this.cartaoCredito.validade.length === 4) {
        mes = this.cartaoCredito.validade.substring(0, 2);
        ano = this.cartaoCredito.validade.substring(2, 4);
      }

      this.saving = true;
      abp.ui.setBusy();

      const subA = this._assinaturaService.getBanderiaCartao(this.cartaoCredito.numeroCartao.substring(0, 6)).pipe(
        finalize(() => {
          abp.ui.clearBusy();
          this.saving = false;
        })
      ).subscribe({
        next: (result) => {
          if (result) {
            this.assinatura.cartaoCredito.emissor = result;

            const subB = this._assinaturaService.getTokenCartao(
              this.cartaoCredito.nome,
              this.cartaoCredito.cpf,
              this.cartaoCredito.numeroCartao,
              Number(mes),
              Number(`20${ano}`),
              this.cartaoCredito.ccv
            ).pipe(
              finalize(() => {
                this.saving = false;
                abp.ui.clearBusy();
              })
            ).subscribe({
              next: (token) => {
                if (token) {
                  this.assinatura.cartaoCredito.cardToken = token;
                  this.assinatura.cartaoCredito.nome = this.cartaoCredito.nome;
                  this.assinatura.cartaoCredito.cpf = this.cartaoCredito.cpf;

                  resolve(true);
                } else {
                  resolve(false);
                }
              },
              error: (err) => {
                console.error('Erro ao obter token do cartão:', err);
                reject(err);
              }
            });

            this.subscriptions.push(subB);
          } else {
            resolve(false);
          }
        },
        error: (err) => {
          console.error('Erro ao obter bandeira do cartão:', err);
          reject(err);
        }
      });

      this.subscriptions.push(subA);
    });
  }

  protected hasBoleto(): boolean {
    if ((this.isAnual && this.plano.valorAnual > 4) || (!this.isAnual && this.plano.valorMensal > 4)) return true;
    return false;
  }

  visualizarSituacaoPagamento(userId: number, assinatura: AssinaturaDto): void {
    this._modalService.show(
      ViewSituacaoPagamentoDialogComponent,
      {
        class: 'modal-lg',
        initialState: {
          userId: userId,
          assinatura: assinatura
        },
      }
    );
  }

  private getRota(): string {
    const role = this.appSession.getRoles()[0].toUpperCase();
    switch (role) {
      case "PACIENTE":
        return "paciente";
      case "FUNCIONARIO":
        return "funcionario";
      case "CLINICA":
        return "clinica";
      case "PROFISSIONALSAUDE":
        return "profissional-saude";
      default:
        return "administrativo";
    }
  }

  onSelectCep($event) {
    this.desabilitarCamposEndereco = true;
    this.endereco.zipCode = $event;
    if ($event.length == 8) {
      this._buscaCepService.getCep($event).subscribe(x => {
        if (x['erro']) {
          abp.message.error("CEP inválido");
        } else {
          this.endereco.streetName = x['logradouro'];
          this.endereco.city = x['localidade'];
          this.endereco.neighborhood = x['bairro'];
          this.endereco.federalUnit = x['uf'].toUpperCase();
          this.desabilitarCamposEndereco = false;
        }
      });
    }
    else {
      this.endereco.streetName = '';
      this.endereco.city = '';
      this.endereco.neighborhood = '';
      this.endereco.federalUnit = '';
      this.desabilitarCamposEndereco = true;
    }
  }

  voltar(): void {
  this.location.back();
  }
}
